import { entryPath, parseRoute } from "./routing.js";

const HISTORY_KEY = "kinloGuest";

function scrollPosition(value) {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

/** Keep a gallery visit to one history entry and retain the reader's place. */
export function createGuestNavigation(browser = window) {
  const { history } = browser;
  const currentPath = () => `${browser.location.pathname}${browser.location.search}${browser.location.hash}`;
  const currentState = () => history.state?.[HISTORY_KEY] || {};
  let feedScrollY = parseRoute(browser.location.pathname).view === "feed"
    ? scrollPosition(currentState().scrollY ?? browser.scrollY)
    : 0;

  history.scrollRestoration = "manual";
  if (!history.state?.[HISTORY_KEY]) {
    history.replaceState({
      ...history.state,
      [HISTORY_KEY]: { scrollY: scrollPosition(browser.scrollY) },
    }, "", currentPath());
  }

  function rememberScroll() {
    const scrollY = scrollPosition(browser.scrollY);
    if (parseRoute(browser.location.pathname).view === "feed") feedScrollY = scrollY;
    history.replaceState({
      ...history.state,
      [HISTORY_KEY]: { ...currentState(), scrollY },
    }, "", currentPath());
  }

  function navigate(path, { replace = false } = {}) {
    const destination = new URL(path, browser.location.href);
    if (!path.includes("?")) destination.search = browser.location.search;
    const nextPath = `${destination.pathname}${destination.search}${destination.hash}`;
    if (nextPath === currentPath()) return;

    rememberScroll();
    const previousRoute = parseRoute(browser.location.pathname);
    const nextRoute = parseRoute(destination.pathname);
    const galleryStep = previousRoute.view === "media" && nextRoute.view === "media"
      && previousRoute.entryId === nextRoute.entryId;
    const replaceCurrent = replace || galleryStep;
    const navigationState = {
      returnTo: replaceCurrent ? currentState().returnTo : currentPath(),
      scrollY: galleryStep ? currentState().scrollY : nextRoute.view === "feed" ? feedScrollY : 0,
    };
    const state = {
      ...(replaceCurrent ? history.state : {}),
      [HISTORY_KEY]: navigationState,
    };
    if (replaceCurrent) history.replaceState(state, "", nextPath);
    else history.pushState(state, "", nextPath);
    browser.dispatchEvent(new Event("popstate"));
  }

  function goBack() {
    if (currentState().returnTo) {
      rememberScroll();
      history.back();
      return;
    }
    // A fresh shared link has no in-app origin to return to.
    const route = parseRoute(browser.location.pathname);
    navigate(route.view === "media" ? entryPath(route.entryId) : "/", { replace: true });
  }

  function restoreScroll() {
    browser.scrollTo({ top: scrollPosition(currentState().scrollY), left: 0, behavior: "instant" });
  }

  return { navigate, goBack, rememberScroll, restoreScroll };
}
